/**
 * P9-01 · Shared analytics vocabulary — client-safe (no server-only imports),
 * so the edge middleware, the browser emitter, and the server ingest procedure
 * all agree on cookie names, consent buckets, and event types.
 */

/**
 * The anonymous-visitor cookie.
 *
 * P17A-06 · Minted in the BROWSER (`./anon-id.ts`), on the first event that
 * needs it — not at the edge, which is where P9-01 put it. A `Set-Cookie` on
 * the storefront's first document is what made every page unstorable by a
 * shared cache; see `docs/rendering-and-caching.md`.
 */
export const VX_ANON_COOKIE = "vx_anon";

/**
 * P11-10 · The consent cookie, written by the banner (`consent-banner.tsx`).
 *
 * **Absent or unrecognised now means `none` — no behavioural capture.** It
 * previously meant `analytics`, i.e. capture-by-default, which ran first-party
 * behavioural analytics on every visitor before they were ever asked. That was
 * only ever defensible while no banner existed to ask; now that one does, the
 * permissive fallback is the wrong way round.
 *
 * The value is a single token, unchanged in shape, but the set gained `all` so
 * the two grants can be expressed independently. That matters because the
 * cookie is not just a record of what was chosen — it is replayed into Consent
 * Mode on every page load, so a returning visitor who allowed ads but declined
 * analytics has to be distinguishable from one who allowed both.
 */
export const VX_CONSENT_COOKIE = "vx_consent";

/**
 * `none` — nothing beyond strictly necessary.
 * `analytics` — first-party behavioural capture + attribution, no ad signals.
 * `marketing` — ad/marketing signals only, no behavioural capture.
 * `all` — both.
 */
export const CONSENT_BUCKETS = ["analytics", "marketing", "none", "all"] as const;
export type ConsentBucket = (typeof CONSENT_BUCKETS)[number];

/** How long a stored choice stands before we ask again. */
export const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * Attribution, identity, and third-party forwarding all require analytics
 * consent — this is the single gate for "may we associate this with a person".
 */
export function attributionAllowed(bucket: ConsentBucket): boolean {
  return bucket === "analytics" || bucket === "all";
}

/** Whether first-party behavioural events may be captured at all. */
export function analyticsGranted(bucket: ConsentBucket): boolean {
  return bucket === "analytics" || bucket === "all";
}

/** Whether ad/marketing signals (GA4 ad_storage, Meta Pixel) may fire. */
export function marketingGranted(bucket: ConsentBucket): boolean {
  return bucket === "marketing" || bucket === "all";
}

/** Collapse two independent grants into the stored token. */
export function bucketFor(grants: {
  analytics: boolean;
  marketing: boolean;
}): ConsentBucket {
  if (grants.analytics && grants.marketing) return "all";
  if (grants.analytics) return "analytics";
  if (grants.marketing) return "marketing";
  return "none";
}

/** Parse a raw cookie value, defaulting to `none` for anything unrecognised. */
export function parseConsentBucket(raw: string | null | undefined): ConsentBucket {
  return (CONSENT_BUCKETS as readonly string[]).includes(raw ?? "")
    ? (raw as ConsentBucket)
    : "none";
}

/**
 * Event types a browser may emit. `PURCHASE` / `REFUND` are deliberately absent:
 * money is server-authoritative (emitted from order/refund data, never trusted
 * from a client — see src/server/analytics/emit.ts).
 */
export const CLIENT_EVENT_TYPES = [
  "PAGE_VIEW",
  "PRODUCT_VIEW",
  "SEARCH",
  "ADD_TO_CART",
  "REMOVE_FROM_CART",
  "CART_VIEW",
  "CHECKOUT_START",
  "CHECKOUT_STEP",
  "CHECKOUT_STEP_COMPLETE",
  "WISHLIST_ADD",
  // P11-23 · Market choice. These are preference, not commerce — they are
  // deliberately NOT in `EVENT_MAP`, so they stay first-party and are never
  // forwarded to GA4/Meta. That omission is a decision, not an oversight:
  // MARKET_SUGGESTED carries a geo-derived country, and shipping
  // location-derived preference to third parties is precisely what the consent
  // posture exists to minimise. If a funnel need ever justifies forwarding, it
  // gets its own decision then.
  "I18N_LANGUAGE_CHANGED",
  "I18N_CURRENCY_CHANGED",
  "I18N_REGION_CHANGED",
  "MARKET_SUGGESTED",
  "MARKET_SUGGESTION_OVERRIDDEN",
] as const;
export type ClientEventType = (typeof CLIENT_EVENT_TYPES)[number];

/** First-touch / last-touch campaign attribution, read from the URL client-side. */
export type Attribution = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  referrer?: string;
  landingPath?: string;
};

/** One event as the browser sends it. `occurredAt` is advisory — the server stamps time. */
export type ClientEvent = {
  type: ClientEventType;
  /** Shared de-dup id (P9-05): the browser fires its provider tags with the same
   * id the server forwards under, so GA4/Meta count the event once. */
  eventId?: string;
  path?: string;
  productId?: string;
  variantId?: string;
  searchQuery?: string;
  quantity?: number;
  props?: Record<string, unknown>;
};
